import React, { FC, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  LinearProgress,
  Tooltip,
  Fade,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// ─── Types ────────────────────────────────────────────────────────────────────

interface StaffEntry {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  medic_type: string;
  gender: string;
  state: string;
  city: string;
  dob: string;
  folio_no: string;
  years_of_experience: string;
  specialties: string;
  job_title: string;
  department: string;
  employment_type: string;
  work_type: string;
  contract_duration: string;
  start_date: string;
  end_date: string;
  salary: string;
  note: string;
}

interface StaffEntryResult {
  index: number;
  email: string;
  status: "success" | "error";
  message: string;
}

interface AddStaffModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  token: string;
  apiAddExistingEmployees: (fields: {
    token: string;
    employees: Omit<StaffEntry, "id">[];
  }) => Promise<any>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

interface Option {
  value: string;
  label: string;
}

const MEDIC_TYPES: Option[] = [
  { value: "doctor", label: "Doctor" },
  { value: "nurse", label: "Nurse" },
  { value: "midwife", label: "Midwife" },
  { value: "nurse/midwife", label: "Nurse/Midwife" },
];

const JOB_TITLES: Option[] = [
  { value: "doctor", label: "Doctor" },
  { value: "nurse", label: "Nurse" },
  { value: "midwife", label: "Midwife" },
  { value: "nurse/midwife", label: "Nurse/Midwife" },
];

const DEPARTMENTS: Option[] = [
  { value: "General Medicine", label: "General Medicine" },
  { value: "Surgery", label: "Surgery" },
  {
    value: "Obstetrics and Gynaecology (O & G)",
    label: "Obstetrics and Gynaecology (O & G)",
  },
  { value: "Pediatrics", label: "Pediatrics" },
  { value: "Radiology/Imaging", label: "Radiology/Imaging" },
  { value: "Pharmacy", label: "Pharmacy" },
  { value: "Labortory Services", label: "Labortory Services" },
  {
    value: "Emergency Department (A & E)",
    label: "Emergency Department (A & E)",
  },
  { value: "Internal Medicine", label: "Internal Medicine" },
  { value: "Intensive Care Unit (ICU)", label: "Intensive Care Unit (ICU)" },
  { value: "Anesthesia", label: "Anesthesia" },
  { value: "Orthopedics", label: "Orthopedics" },
  { value: "Cardiology", label: "Cardiology" },
  {
    value: "Physiotheraphy/Rehabilitation",
    label: "Physiotheraphy/Rehabilitation",
  },
  { value: "Dental Care", label: "Dental Care" },
  { value: "Ophthalmology (Eye Care)", label: "Ophthalmology (Eye Care)" },
  {
    value: "Ear, Nose, and Throat (ENT)",
    label: "Ear, Nose, and Throat (ENT)",
  },
  { value: "Dermatology", label: "Dermatology" },
  { value: "Urology", label: "Urology" },
  { value: "Oncology", label: "Oncology" },
  { value: "Mental Health/Psychiatry", label: "Mental Health/Psychiatry" },
  {
    value: "Dietary and Nutrition Services",
    label: "Dietary and Nutrition Services",
  },
  { value: "Administration and Billing", label: "Administration and Billing" },
  { value: "Infection Control", label: "Infection Control" },
  {
    value: "Outpatient Department (OPD)",
    label: "Outpatient Department (OPD)",
  },
  { value: "Nephrology/Dialysis Unit", label: "Nephrology/Dialysis Unit" },
  { value: "Geriatrics", label: "Geriatrics" },
  {
    value: "Family Planning and Reproductive Health",
    label: "Family Planning and Reproductive Health",
  },
  {
    value: "Public Health/Preventive Care",
    label: "Public Health/Preventive Care",
  },
  { value: "Other", label: "Other" },
];

const SPECIALTIES: Option[] = [
  {
    value: "Obstetricis and Gynaecology",
    label: "Obstetricis and Gynaecology",
  },
  { value: "Cardiology", label: "Cardiology" },
  { value: "Endocrinology", label: "Endocrinology" },
  { value: "Gastroenterology", label: "Gastroenterology" },
  { value: "Hematology", label: "Hematology" },
  { value: "Infectious Disease", label: "Infectious Disease" },
  { value: "Nephrology", label: "Nephrology" },
  { value: "Oncology", label: "Oncology" },
  { value: "Pulmonology", label: "Pulmonology" },
  { value: "Rheumatology", label: "Rheumatology" },
  { value: "General Surgery", label: "General Surgery" },
  { value: "Cardiothoracic Surgery", label: "Cardiothoracic Surgery" },
  { value: "Neurosurgery", label: "Neurosurgery" },
  { value: "Orthopedic Surgery", label: "Orthopedic Surgery" },
  { value: "Ophthalmology", label: "Ophthalmology" },
  { value: "Otolaryngolory (ENT)", label: "Otolaryngolory (ENT)" },
  { value: "Plastic Surgery", label: "Plastic Surgery" },
  { value: "Vascular Surgery", label: "Vascular Surgery" },
  { value: "Neonatology", label: "Neonatology" },
  { value: "Psychiatry", label: "Psychiatry" },
  { value: "Dermatology", label: "Dermatology" },
  { value: "Radiology", label: "Radiology" },
  { value: "General Anesthesiology", label: "General Anesthesiology" },
  { value: "Neurology", label: "Neurology" },
  { value: "Pathology", label: "Pathology" },
  { value: "Family Medicine (GP)", label: "Family Medicine (GP)" },
  { value: "No Specialty", label: "No Specialty" },
];

const EMPLOYMENT_TYPES: Option[] = [
  { value: "full time", label: "Full Time" },
  { value: "short locum", label: "Short Locum" },
  { value: "long locum", label: "Long Locum" },
];

const WORK_TYPES: Option[] = [
  { value: "on-site", label: "On-Site" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
];

const GENDERS: Option[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const REQUIRED_FIELDS: (keyof StaffEntry)[] = [
  "email",
  "first_name",
  "last_name",
  "medic_type",
  "job_title",
  "employment_type",
  "work_type",
  "start_date",
  "salary",
];

const emptyEntry = (): StaffEntry => ({
  id: crypto.randomUUID(),
  email: "",
  first_name: "",
  last_name: "",
  phone_number: "",
  medic_type: "",
  gender: "",
  state: "",
  city: "",
  dob: "",
  folio_no: "",
  years_of_experience: "",
  specialties: "",
  job_title: "",
  department: "",
  employment_type: "",
  work_type: "",
  contract_duration: "",
  start_date: "",
  end_date: "",
  salary: "",
  note: "",
});

const getEntryErrors = (
  entry: StaffEntry,
): Partial<Record<keyof StaffEntry, string>> => {
  const entryErrors: Partial<Record<keyof StaffEntry, string>> = {};

  REQUIRED_FIELDS.forEach((field) => {
    if (!entry[field]) entryErrors[field] = "This field is required";
  });

  if (entry.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry.email)) {
    entryErrors.email = "Must be a valid email";
  }

  if (entry.employment_type !== "full time" && !entry.contract_duration) {
    entryErrors.contract_duration =
      "Contract duration is required for locum staff";
  }

  return entryErrors;
};

const inputSx = (error?: boolean) => ({
  "& .MuiOutlinedInput-root": {
    fontFamily: "Lexend",
    fontSize: 13,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF", // ← solid white, never transparent
    "& fieldset": {
      borderColor: error ? "#F44336" : "#DDE3F0",
    },
    "&:hover fieldset": { borderColor: "#2F66F4" },
    "&.Mui-focused fieldset": { borderColor: "#2F66F4", borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontFamily: "Lexend", fontSize: 13 },
  "& .MuiFormHelperText-root": { fontFamily: "Lexend", fontSize: 11, mt: 0.5 },
});

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabel: FC<{ label: string; first?: boolean }> = ({
  label,
  first,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    sx={{ mb: 2.5, mt: first ? 0 : 4, px: 0.5 }}
  >
    <Box
      sx={{
        width: 3,
        height: 16,
        borderRadius: 1,
        background: "linear-gradient(180deg, #2F66F4 0%, #5B8AF7 100%)",
        flexShrink: 0,
      }}
    />
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#8A94A6",
        fontFamily: "Lexend",
      }}
    >
      {label}
    </Typography>
  </Stack>
);

// ─── Field ────────────────────────────────────────────────────────────────────

const Field: FC<{
  label: string;
  value: string;
  field: keyof StaffEntry;
  required?: boolean;
  type?: string;
  select?: boolean;
  options?: Option[];
  onChange: (field: keyof StaffEntry, value: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}> = ({
  label,
  value,
  field,
  required,
  type = "text",
  select,
  options,
  onChange,
  error,
  helperText,
  disabled,
}) => {
  if (select) {
    return (
      <FormControl
        fullWidth
        size="small"
        error={error}
        sx={inputSx(error)}
        disabled={disabled}
      >
        <InputLabel sx={{ fontFamily: "Lexend", fontSize: 13 }}>
          {label}
          {required ? " *" : ""}
        </InputLabel>
        <Select
          value={value}
          label={`${label}${required ? " *" : ""}`}
          onChange={(e) => onChange(field, e.target.value)}
          sx={{
            fontFamily: "Lexend",
            fontSize: 13,
            backgroundColor: "#FFFFFF",
          }}
          MenuProps={{
            PaperProps: {
              sx: { backgroundColor: "#FFFFFF", maxHeight: 260 },
            },
          }}
        >
          {options?.map((opt) => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{ fontFamily: "Lexend", fontSize: 13 }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <TextField
      fullWidth
      size="small"
      label={`${label}${required ? " *" : ""}`}
      value={value}
      type={type}
      error={error}
      helperText={helperText}
      disabled={disabled}
      onChange={(e) => onChange(field, e.target.value)}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      sx={inputSx(error)}
    />
  );
};

// ─── Staff Card ───────────────────────────────────────────────────────────────

const StaffCard: FC<{
  entry: StaffEntry;
  index: number;
  total: number;
  errors: Partial<Record<keyof StaffEntry, string>>;
  expanded: boolean;
  onToggle: () => void;
  onChange: (id: string, field: keyof StaffEntry, value: string) => void;
  onRemove: (id: string) => void;
}> = ({ entry, index, total, errors, expanded, onToggle, onChange, onRemove }) => {
  const hasErrors = Object.keys(errors).length > 0;
  const isRequiredComplete = REQUIRED_FIELDS.every((f) => !!entry[f]);
  const handle = (field: keyof StaffEntry, value: string) =>
    onChange(entry.id, field, value);
  const summary = [
    entry.job_title,
    entry.employment_type,
    entry.start_date,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box
      sx={{
        border: hasErrors ? "1.5px solid #F4433640" : "1.5px solid #E0E7F5",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#FFFFFF", // ← solid white card
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 20px rgba(47,102,244,0.08)" },
      }}
    >
      {/* Card Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={onToggle}
        sx={{
          px: 3.25,
          py: 2.1,
          cursor: "pointer",
          backgroundColor: hasErrors ? "#FFF8F8" : "#F4F7FF", // ← solid header bg
          borderBottom: expanded ? "1px solid #E0E7F5" : "none",
          userSelect: "none",
        }}
      >
        <Stack direction="row" alignItems="center" gap={0}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              background: "linear-gradient(135deg, #2F66F4 0%, #5B8AF7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "Lexend",
              }}
            >
              {index + 1}
            </Typography>
          </Box>
          <Box sx={{ pl: 2 }}>
            <Typography
              sx={{
                fontFamily: "Lexend",
                fontWeight: 600,
                fontSize: 14,
                color: "#1A2340",
                lineHeight: 1.25,
                mb: entry.email || (!expanded && summary) ? 0.5 : 0,
              }}
            >
              {entry.first_name || entry.last_name
                ? `${entry.first_name} ${entry.last_name}`.trim()
                : `Employee ${index + 1}`}
            </Typography>
            {entry.email && (
              <Typography
                sx={{
                  fontFamily: "Lexend",
                  fontSize: 11,
                  lineHeight: 1.35,
                  color: "#8A94A6",
                }}
              >
                {entry.email}
              </Typography>
            )}
            {!expanded && summary && (
              <Typography
                sx={{ fontFamily: "Lexend", fontSize: 11, color: "#5F6C86" }}
              >
                {summary}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          {isRequiredComplete && !hasErrors && (
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#22C55E" }} />
          )}
          {hasErrors && (
            <Tooltip title={`${Object.keys(errors).length} field(s) required`}>
              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#F44336" }} />
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{
              color: "#7E8AA5",
              "&:hover": { background: "#EAF0FF", color: "#2F66F4" },
            }}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
          {total > 1 && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(entry.id);
              }}
              sx={{
                color: "#CBD2E0",
                "&:hover": { color: "#F44336", background: "#FFF0F0" },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Card Body */}
      {expanded && (
        <Box sx={{ px: 3.5, py: 3.25, backgroundColor: "#FFFFFF" }}>
          <SectionLabel label="Personal Information" first />

          {/* CSS grid — guaranteed 16px column gap + 20px row gap, no MUI margin quirks */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: "20px",
              rowGap: "22px",
            }}
          >
            <Field
              label="First Name"
              field="first_name"
              value={entry.first_name}
              required
              onChange={handle}
              error={!!errors.first_name}
              helperText={errors.first_name}
            />
            <Field
              label="Last Name"
              field="last_name"
              value={entry.last_name}
              required
              onChange={handle}
              error={!!errors.last_name}
              helperText={errors.last_name}
            />
            <Field
              label="Email Address"
              field="email"
              value={entry.email}
              required
              type="email"
              onChange={handle}
              error={!!errors.email}
              helperText={errors.email}
            />
            <Field
              label="Phone Number"
              field="phone_number"
              value={entry.phone_number}
              onChange={handle}
            />
            <Field
              label="Medic Type"
              field="medic_type"
              value={entry.medic_type}
              required
              select
              options={MEDIC_TYPES}
              onChange={handle}
              error={!!errors.medic_type}
              helperText={errors.medic_type}
            />
            <Field
              label="Gender"
              field="gender"
              value={entry.gender}
              select
              options={GENDERS}
              onChange={handle}
            />
            <Field
              label="Date of Birth"
              field="dob"
              value={entry.dob}
              type="date"
              onChange={handle}
            />
            <Field
              label="Years of Experience"
              field="years_of_experience"
              value={entry.years_of_experience}
              type="number"
              onChange={handle}
            />
            <Field
              label="Folio / License No."
              field="folio_no"
              value={entry.folio_no}
              onChange={handle}
            />
            <Field
              label="Specialty"
              field="specialties"
              value={entry.specialties}
              select
              options={SPECIALTIES}
              onChange={handle}
            />
            <Field
              label="State"
              field="state"
              value={entry.state}
              onChange={handle}
            />
            <Field
              label="City"
              field="city"
              value={entry.city}
              onChange={handle}
            />
          </Box>

          <SectionLabel label="Employment Terms" />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              columnGap: "20px",
              rowGap: "22px",
            }}
          >
            <Field
              label="Job Title"
              field="job_title"
              value={entry.job_title}
              required
              select
              options={JOB_TITLES}
              onChange={handle}
              error={!!errors.job_title}
              helperText={errors.job_title}
            />
            <Field
              label="Department"
              field="department"
              value={entry.department}
              select
              options={DEPARTMENTS}
              onChange={handle}
            />
            <Field
              label="Employment Type"
              field="employment_type"
              value={entry.employment_type}
              required
              select
              options={EMPLOYMENT_TYPES}
              onChange={handle}
              error={!!errors.employment_type}
              helperText={errors.employment_type}
            />
            <Field
              label="Work Type"
              field="work_type"
              value={entry.work_type}
              required
              select
              options={WORK_TYPES}
              onChange={handle}
              error={!!errors.work_type}
              helperText={errors.work_type}
            />
            {entry.employment_type !== "full time" && (
              <Field
                label="Contract Duration"
                field="contract_duration"
                value={entry.contract_duration}
                onChange={handle}
                error={!!errors.contract_duration}
                helperText={errors.contract_duration}
              />
            )}
            <Field
              label="Start Date"
              field="start_date"
              value={entry.start_date}
              required
              type="date"
              onChange={handle}
              error={!!errors.start_date}
              helperText={errors.start_date}
            />
            <Field
              label="End Date"
              field="end_date"
              value={entry.end_date}
              type="date"
              onChange={handle}
            />
            <Field
              label="Salary (₦)"
              field="salary"
              value={entry.salary}
              required
              type="number"
              onChange={handle}
              error={!!errors.salary}
              helperText={errors.salary}
            />
            {/* Note spans full width */}
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <Field
                label="Note"
                field="note"
                value={entry.note}
                onChange={handle}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Results View ─────────────────────────────────────────────────────────────

const ResultsView: FC<{ results: StaffEntryResult[]; onClose: () => void }> = ({
  results,
  onClose,
}) => {
  const success = results.filter((r) => r.status === "success");
  const failed = results.filter((r) => r.status === "error");

  return (
    <Box sx={{ p: 1 }}>
      <Stack alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: success.length > 0 ? "#F0FDF4" : "#FFF0F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${success.length > 0 ? "#86EFAC" : "#FECACA"}`,
          }}
        >
          {success.length > 0 ? (
            <CheckCircleOutlineIcon sx={{ color: "#22C55E", fontSize: 28 }} />
          ) : (
            <ErrorOutlineIcon sx={{ color: "#F44336", fontSize: 28 }} />
          )}
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: "Lexend",
              fontWeight: 700,
              fontSize: 17,
              color: "#1A2340",
            }}
          >
            {success.length} of {results.length} added successfully
          </Typography>
          {failed.length > 0 && (
            <Typography
              sx={{
                fontFamily: "Lexend",
                fontSize: 13,
                color: "#8A94A6",
                mt: 0.5,
              }}
            >
              {failed.length} employee{failed.length > 1 ? "s" : ""} could not
              be added
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack gap={1.5} sx={{ maxHeight: 300, overflowY: "auto", pr: 0.5 }}>
        {results.map((r, i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{
              p: 1.5,
              borderRadius: "10px",
              backgroundColor: r.status === "success" ? "#F0FDF4" : "#FFF8F8",
              border: `1px solid ${r.status === "success" ? "#BBF7D0" : "#FECACA"}`,
            }}
          >
            {r.status === "success" ? (
              <CheckCircleOutlineIcon
                sx={{ fontSize: 16, color: "#22C55E", flexShrink: 0 }}
              />
            ) : (
              <ErrorOutlineIcon
                sx={{ fontSize: 16, color: "#F44336", flexShrink: 0 }}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "Lexend",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#1A2340",
                }}
              >
                {r.email}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Lexend",
                  fontSize: 11,
                  color: r.status === "success" ? "#16A34A" : "#DC2626",
                }}
              >
                {r.message}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Button
        fullWidth
        variant="contained"
        onClick={onClose}
        sx={{
          mt: 3,
          height: 44,
          borderRadius: "10px",
          fontFamily: "Lexend",
          fontWeight: 600,
          fontSize: 14,
          textTransform: "none",
          background: "linear-gradient(135deg, #2F66F4 0%, #5B8AF7 100%)",
          boxShadow: "0 4px 14px rgba(47,102,244,0.3)",
          "&:hover": {
            background: "linear-gradient(135deg, #2459E0 0%, #4A7BF5 100%)",
          },
        }}
      >
        Done
      </Button>
    </Box>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const AddStaffModal: FC<AddStaffModalProps> = ({
  open,
  onClose,
  onSuccess,
  token,
  apiAddExistingEmployees,
}) => {
  const initialEntryRef = useRef<StaffEntry>(emptyEntry());
  const [entries, setEntries] = useState<StaffEntry[]>([
    initialEntryRef.current,
  ]);
  const [expandedEntries, setExpandedEntries] = useState<Record<string, boolean>>(
    { [initialEntryRef.current.id]: true },
  );
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, Partial<Record<keyof StaffEntry, string>>>
  >({});
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<StaffEntryResult[] | null>(null);

  const handleAdd = () => {
    const nextEntry = emptyEntry();
    setEntries((prev) => [...prev, nextEntry]);
    setExpandedEntries((prev) =>
      Object.fromEntries([
        ...Object.keys(prev).map((entryId) => [entryId, false] as const),
        [nextEntry.id, true] as const,
      ]),
    );
  };

  const handleRemove = (id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    setExpandedEntries((prev) => {
      const next = {...prev};
      delete next[id];
      const remainingIds = Object.keys(next);
      if (remainingIds.length === 1) {
        next[remainingIds[0]] = true;
      }
      return next;
    });
  };

  const handleToggle = (id: string) =>
    setExpandedEntries((prev) => ({...prev, [id]: !prev[id]}));

  const handleChange = useCallback(
    (id: string, field: keyof StaffEntry, value: string) => {
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
      );
      setFieldErrors((prev) => {
        const next = { ...prev };
        if (next[id]) {
          const { [field]: _, ...rest } = next[id];
          next[id] = rest;
        }
        return next;
      });
    },
    [],
  );

  const validate = (): boolean => {
    const errors: Record<
      string,
      Partial<Record<keyof StaffEntry, string>>
    > = {};
    entries.forEach((e) => {
      const entryErrors = getEntryErrors(e);
      if (Object.keys(entryErrors).length > 0) errors[e.id] = entryErrors;
    });

    const seenEmails = new Map<string, string>();
    entries.forEach((entry) => {
      const normalizedEmail = entry.email.trim().toLowerCase();
      if (!normalizedEmail) return;
      const existingId = seenEmails.get(normalizedEmail);
      if (existingId) {
        errors[entry.id] = {
          ...(errors[entry.id] ?? {}),
          email: "Duplicate email in this batch",
        };
        errors[existingId] = {
          ...(errors[existingId] ?? {}),
          email: "Duplicate email in this batch",
        };
      } else {
        seenEmails.set(normalizedEmail, entry.id);
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    const employees = entries.map(({ id, ...rest }) => ({
      ...rest,
      salary: Number(rest.salary),
      years_of_experience: rest.years_of_experience
        ? Number(rest.years_of_experience)
        : undefined,
      dob: rest.dob || undefined,
      folio_no: rest.folio_no || undefined,
      specialties: rest.specialties || undefined,
      phone_number: rest.phone_number || undefined,
      gender: rest.gender || undefined,
      state: rest.state || undefined,
      city: rest.city || undefined,
      department: rest.department || undefined,
      end_date: rest.end_date || undefined,
      contract_duration: rest.contract_duration || undefined,
      note: rest.note || undefined,
    }));

    try {
      const res = await apiAddExistingEmployees({ token, employees } as any);
      if (res?.status === "error") {
        setResults([
          {
            index: 0,
            email: "All employees",
            status: "error",
            message: res?.msg ?? "Request failed",
          },
        ]);
      } else {
        const resultData: StaffEntryResult[] =
          (res?.data?.data?.results as StaffEntryResult[] | undefined)?.sort(
            (a, b) => a.index - b.index,
          ) ??
          employees.map((e, i) => ({
            index: i,
            email: e.email,
            status: "success" as const,
            message: "Added successfully",
          }));
        setResults(resultData);
        onSuccess();
      }
    } catch {
      setResults([
        {
          index: 0,
          email: "All employees",
          status: "error",
          message: "An unexpected error occurred",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    const firstEntry = emptyEntry();
    setEntries([firstEntry]);
    setExpandedEntries({[firstEntry.id]: true});
    setFieldErrors({});
    setResults(null);
    onClose();
  };

  const successCount = entries.filter((e) =>
    Object.keys(getEntryErrors(e)).length === 0,
  ).length;
  const normalizedEmails = entries
    .map((entry) => entry.email.trim().toLowerCase())
    .filter(Boolean);
  const hasDuplicateEmails =
    new Set(normalizedEmails).size !== normalizedEmails.length;
  const liveValidationErrors = entries.reduce((acc, entry) => {
    const nextErrors = getEntryErrors(entry);
    return acc + Object.keys(nextErrors).length;
  }, 0);
  const hasValidationErrors = Object.keys(fieldErrors).length > 0;
  const canSubmit =
    !loading &&
    entries.length > 0 &&
    successCount === entries.length &&
    liveValidationErrors === 0 &&
    !hasDuplicateEmails &&
    !hasValidationErrors;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
          maxHeight: "92vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF", // ← explicit solid white Paper
        },
      }}
      // ↓ solid backdrop, no blur/transparency leaking through
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(10, 18, 40, 0.55)" },
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #1A2340 0%, #2F3E6B 100%)",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: "rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PersonAddAlt1Icon sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: "Lexend",
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#fff",
                }}
              >
                Add Existing Staff
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Lexend",
                  fontSize: 11,
                  color: "rgba(255,255,255,0.6)",
                }}
              >
                {entries.length} employee{entries.length > 1 ? "s" : ""} ·{" "}
                {successCount} complete
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: "rgba(255,255,255,0.7)",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {loading && <LinearProgress sx={{ height: 2 }} />}
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          px: 3.5,
          pt: 4,
          pb: 3.25,
          overflowY: "auto",
          flex: 1,
          backgroundColor: "#F0F4FF",
        }}
      >
        {results ? (
          <ResultsView results={results} onClose={handleClose} />
        ) : (
          <Stack gap={3} sx={{ pt: 0.5 }}>
            {entries.map((entry, index) => (
              <StaffCard
                key={entry.id}
                entry={entry}
                index={index}
                total={entries.length}
                errors={fieldErrors[entry.id] ?? {}}
                expanded={!!expandedEntries[entry.id]}
                onToggle={() => handleToggle(entry.id)}
                onChange={handleChange}
                onRemove={handleRemove}
              />
            ))}
            <Button
              onClick={handleAdd}
              startIcon={<AddIcon />}
              sx={{
                height: 48,
                borderRadius: "10px",
                border: "1.5px dashed #B8C7E8",
                color: "#2F66F4",
                fontFamily: "Lexend",
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                backgroundColor: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#EEF2FF",
                  borderColor: "#2F66F4",
                },
              }}
            >
              Add Another Employee
            </Button>
          </Stack>
        )}
      </DialogContent>

      {/* Footer */}
      {!results && (
        <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #E0E7F5",
          backgroundColor: "#FFFFFF", // ← solid white footer
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
          {Object.keys(fieldErrors).length > 0 && (
            <Typography
              sx={{
                fontFamily: "Lexend",
                fontSize: 12,
                color: "#F44336",
                flex: 1,
              }}
            >
              Please fill in all required fields before submitting.
            </Typography>
          )}
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              px: 3,
              height: 40,
              borderRadius: "10px",
              fontFamily: "Lexend",
              fontWeight: 500,
              fontSize: 13,
              textTransform: "none",
              color: "#5A6482",
              border: "1.5px solid #E0E7F5",
              backgroundColor: "#FFFFFF",
              "&:hover": { backgroundColor: "#F0F2F8", borderColor: "#CBD2E0" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            variant="contained"
            sx={{
              px: 3,
              height: 40,
              borderRadius: "10px",
              fontFamily: "Lexend",
              fontWeight: 600,
              fontSize: 13,
              textTransform: "none",
              background: "linear-gradient(135deg, #2F66F4 0%, #5B8AF7 100%)",
              boxShadow: "0 4px 14px rgba(47,102,244,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #2459E0 0%, #4A7BF5 100%)",
              },
              "&:disabled": { background: "#CBD2E0", boxShadow: "none" },
            }}
          >
            {loading
              ? "Adding Staff..."
              : `Add ${entries.length} Employee${entries.length > 1 ? "s" : ""}`}
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default AddStaffModal;
